import React, { useEffect, useState } from 'react';
import { Button, Grid } from '@mui/material';
import './entryFormPayment.css';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { setPayPalOrderHistory, setStepIndex, setEntryForms } from '../../../redux/reducers/entryFormsSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import EntryFormPrint from '../../atoms/entryFormPrint/entryFormPrint';
import { calcDogEntryFee, calcOwnerEntryFees } from '../../../utils/calculateFees';
import { EntryFormRecordData } from '../../../models/EntryFormRecordData';
import { EntryFormEventInfo } from '../../../models/EntryFormEventInfo';
import { EntryFormRecordDataPayPalTransaction } from '../../../models/EntryFormRecordDataPayPalTransaction';
import axios from 'axios';
import _ from 'lodash';

export default function EntryFormPayment() {

  const [ isLoading, setIsLoading ] = useState(true);
  const [ msg, setMsg ] = useState('');
  const [ clientId, setClientId ] = useState('');
  const [ eventId ] = useState(window.location.pathname === '/' ? '/no-event' : window.location.pathname)

  const { sumCatalogFee, sumEntryFee, totalServiceFee, totalFees, entries, payPalOrderHistory, agreeToAgreementDate } = useAppSelector((state: any) => state.entryForms);
  const { catalogFee, contactEmail, entryEvents, serviceFee, serviceFeeBasis } = useAppSelector((state: any) => state.eventConfig);
  const { email } = useAppSelector((state: any) => state.registrant);

  const dispatch = useAppDispatch();

  const initialOptions = {
    clientId,
    currency: "USD",
    intent: "capture",
    "disable-funding": "paylater",
    "integration-date": "2022-04-13"
    // "data-client-token": "abc123xyz==",
  };

  const printEntryFormPrintInstructions = `You can print the forms to a PDF instead of a printer after clicking the Print Entry Forms button or go back to the Review step and either take a screenshot or picture.`;

  useEffect(() => {
    async function fetchData() {
      try {
        const getClientIdRsp = await axios.get(`/api/payment/${email}`, { params: { eventId: eventId.replace('/', '') }});
        setClientId(_.get(getClientIdRsp, 'data.payPalClientId', ''));
        const updatedEntries = _.cloneDeep(entries);
        updatedEntries.forEach((updatedEntry: EntryFormRecordData) => {
          const calcDogEntryFeeResult = calcDogEntryFee(updatedEntry, entryEvents);
          updatedEntry.entryFee = calcDogEntryFeeResult.entryFee;
          if (calcDogEntryFeeResult.msgs.length > 0) {
            axios.post(`/api/logClientEvent/${eventId}`, JSON.stringify(calcDogEntryFeeResult.msgs), { params: { email }})
          }
        });
        const calcOwnerEntryFeesResult = calcOwnerEntryFees(updatedEntries, serviceFee, serviceFeeBasis, sumCatalogFee);
        setIsLoading(false);
        dispatch(setEntryForms({totalFees: calcOwnerEntryFeesResult.totalFees, sumEntryFee: calcOwnerEntryFeesResult.sumEntryFee, totalServiceFee: calcOwnerEntryFeesResult.sumServiceFee }));
        if (calcOwnerEntryFeesResult.msgs.length > 0) {
            axios.post(`/api/logClientEvent/${eventId}`, JSON.stringify(calcOwnerEntryFeesResult.msgs), { params: { email }})
        }
      } catch(err: any) {
        console.log('get payments err=' + JSON.stringify(err, null, 2));
      }
    }
    if (!clientId) {
      fetchData();
    }
  });
  
  const getClasses = (eventEntries: EntryFormEventInfo[]) => {
    const retValue: string[] = [];
    eventEntries.forEach((eventEntry: EntryFormEventInfo) => {
      eventEntry.classes.forEach((eventEntryClass: any) => {
        if (eventEntryClass.isEntered) {
          retValue.push(`${eventEntry.eventId} : ${eventEntryClass.name} - Jump Ht: ${eventEntryClass.jumpHt ? eventEntryClass.jumpHt : 'blank'}`);
        }
      });
    });
    return retValue;
  }

  const getEntryFee = (entry: EntryFormRecordData) => {
    /* istanbul ignore next */
    return entry.entryFee && typeof entry.entryFee === 'number' ? entry.entryFee.toFixed(2) : 'unknown';
  }

  return (
    <>
    { isLoading ?
      <h1>Loading</h1>
      :
      <>
        <Grid container className='noprint'>
          <Grid key={'entry-form-payment-header'} item md={12}>
            <h2>Please submit payment for the below entries.</h2>
          </Grid>
          { entries.map((entry: EntryFormRecordData, entryIndex: number) => (
            <Grid container direction="column" justifyContent="flex-start" p={2} key={'entry-form-payment-entry-' + entryIndex}>
              <Grid item className="entry-form-payment-dog-header">Call Name: { entry.callName }</Grid>
              <Grid item> Classes Entered:</Grid>
                { getClasses(entry.eventEntries).map((classEntered: string, classIndex: number) => (
                  <Grid key={'entry-form-payment-class-entered-' + classIndex} id={'entry-form-payment-class-entered-' + classIndex}>{ classEntered }</Grid>
                ))}
              <Grid item className="entry-form-payment-bold">Entry Fee for this dog: ${ getEntryFee(entry) }</Grid>
            </Grid>
          ))}
          <Grid item md={12} id="entry-form-payment-total-entry-fees" className="entry-form-payment-dog-header">TOTAL ENTRY FEES (for all dogs): ${sumEntryFee?.toFixed(2)}</Grid>
          { serviceFee ? 
              <Grid item md={12} id="entry-form-payment-total-service-fees" className="entry-form-payment-dog-header">TOTAL SERVICE FEES (for all dogs): ${totalServiceFee?.toFixed(2)}</Grid>
            : <></>
          }
          { catalogFee > 0 && sumCatalogFee > 0 ? 
              <Grid item md={12} id="entry-form-payment-total-catalog-fees" className="entry-form-payment-dog-header">TOTAL CATALOG FEES (for pre-ordered catalog): ${sumCatalogFee?.toFixed(2)}</Grid>
            : <></>
          }
          <Grid item md={12}  id="entry-form-payment-total-fees" className="entry-form-payment-dog-header">TOTAL FEES (for all dogs): ${totalFees?.toFixed(2)}</Grid>
        </Grid>
        <Grid container justifyContent="center" className='noprint'>
          <Grid item md={4} p={2}>
            <PayPalScriptProvider options={initialOptions}>
              <PayPalButtons 
                createOrder={(data: any, actions: any) => {
                  return actions.order
                    .create({
                      purchase_units: [
                        {
                          amount: {
                            currency_code: 'USD',
                            value: `${totalFees}`
                          },
                        },
                      ],
                    })
                    .then((orderId: any) => {
                      return orderId;
                    });
                }}
                onApprove={(data: any, actions: any) => {
                  if (!actions || !actions.order) {
                    return Promise.resolve();
                  }
                  return actions.order.capture().then((details: any) => {
                    const updatedPayPalOrderHistory = _.cloneDeep(payPalOrderHistory);
                    updatedPayPalOrderHistory.push(details);
                    return axios.patch(`/api/payment/${email}`, details).then(() => {
                      dispatch(setPayPalOrderHistory(updatedPayPalOrderHistory));
                      setMsg(`Payment of $${_.get(details, 'purchase_units[0].amount.value')} received from ${_.get(details, 'payer.name.given_name')} ${_.get(details, 'payer.name.surname')}`)
                      dispatch(setStepIndex(3));
                      const payPalOrder = _.cloneDeep(details);
                      const newTransaction: EntryFormRecordDataPayPalTransaction = {
                        eventId: eventId.replace('/', ''),
                        id: _.get(payPalOrder, 'id', ''),
                        shipping: {
                          name: {
                            full_name: _.get(payPalOrder, 'purchase_units[0].shipping.name.full_name', '')
                          },
                          address: {
                            address_line_1: _.get(payPalOrder, 'purchase_units[0].shipping.address.address_line_1', ''),
                            admin_area_2: _.get(payPalOrder, 'purchase_units[0].shipping.address.admin_area_2', ''),
                            admin_area_1: _.get(payPalOrder, 'purchase_units[0].shipping.address.admin_area_1',''),
                            postal_code: _.get(payPalOrder, 'purchase_units[0].shipping.address.postal_code', ''),
                          }
                        },
                        create_time: _.get(payPalOrder, 'create_time', ''),
                        update_time: _.get(payPalOrder, 'update_time', ''),
                        amount: _.get(payPalOrder, 'purchase_units[0].amount.value',''),
                        payer: {
                          name: {
                            given_name: _.get(payPalOrder, 'payer.name.given_name', ''),
                            surname: _.get(payPalOrder, 'payer.name.surname', ''),
                          },
                          email_address: _.get(payPalOrder, 'payer.email_address', ''),
                          payer_id: _.get(payPalOrder, 'payer.payer_id', '')
                        }                            
                      }
                      let myEntries: EntryFormRecordData[] = _.cloneDeep(entries);
                      myEntries.forEach((myEntry: EntryFormRecordData) => {
                        if (!myEntry.payPalTransactions) {
                          myEntry.payPalTransactions = [ newTransaction ];
                        } else {
                          myEntry.payPalTransactions.push(newTransaction);
                        }
                      });
                      return axios.post(`/api/entryForm/${email}`, myEntries, { params: { eventId: eventId.replace('/', ''), sumCatalogFee, totalServiceFee, sumEntryFee, totalFees }});
                    });
                  });
                }}
                onCancel={(data: any) => {
                  const updatedPayPalOrderHistory = _.cloneDeep(payPalOrderHistory);
                  updatedPayPalOrderHistory.push(data);
                  setMsg(`Payment was canceled. Please try again, or print and enter via US Postal service. ${printEntryFormPrintInstructions}`);
                  dispatch(setPayPalOrderHistory(updatedPayPalOrderHistory));
                  return axios.patch(`/api/payment/${email}`, { data, eventId: eventId.replace('/', ''), isError: false, isCancel: true, create_time: new Date().toISOString() });
                }}
                onError={(err: any) => {
                  const updatedPayPalOrderHistory = _.cloneDeep(payPalOrderHistory);
                  updatedPayPalOrderHistory.push(err);
                  setMsg(
                    `Payment encountered an error. Please try again. ` +
                    `If the payment step fails a second time, please print the forms and email them to ${contactEmail}. ${printEntryFormPrintInstructions}`);
                  dispatch(setPayPalOrderHistory(updatedPayPalOrderHistory));
                  return axios.patch(`/api/payment/${email}`, { err, eventId: eventId.replace('/', ''), isError: true, isCancel: false, create_time: new Date().toISOString() });
                }}
              />
            </PayPalScriptProvider>
          </Grid>
          <Grid item md={10} p={2}>
            { msg !== '' ?
              <div>
                <span className='entry-form-payment-clear-msg'>{msg}</span>
                <Button color="primary" style={{ textTransform: 'none'}} variant="outlined" onClick={(e: any) => setMsg('')}>Clear</Button>
              </div>
              : null
            }
          </Grid>
        </Grid>
        <EntryFormPrint />
      </>
    }
    </>
  )
}
